/**
 * The agent's client side (04 §2, 05 §5).
 *
 * A turn is a POST that streams. `fetch` + a reader rather than `EventSource`, because
 * EventSource cannot POST and the message has to go in a body — and because this way the
 * CSRF header and the cookie travel exactly as they do on every other call.
 *
 * `ui_action`s arriving with a turn are what makes the chat part of the app rather than a
 * widget beside it: the agent says "here are three" and the rail actually fills.
 */

import { useCallback, useRef, useState } from 'react'

export interface UIAction {
  type: 'show_rail' | 'open_cart' | 'start_render' | 'ask_choice' | 'directions_pending'
  slot?: string
  product_ids?: string[]
  render_id?: string
  prompt?: string
}

export interface AgentMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  pending?: boolean
  actions?: UIAction[]
}

export interface AgentEvent {
  event: 'token' | 'tool' | 'intent' | 'done' | 'error'
  text?: string
  tool?: string
  intent?: string
  ui_actions?: UIAction[]
  message?: string
}

let csrf: string | null = null
export function setAgentCsrf(token: string | null): void {
  csrf = token
}

export interface SendOptions {
  sessionId: string
  message: string
  roomId?: string | null
  maskId?: string | null
  onToken: (text: string) => void
  onTool?: (tool: string) => void
  onDone: (actions: UIAction[]) => void
  onError?: (message: string) => void
  signal?: AbortSignal
}

export async function sendMessage(options: SendOptions): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (csrf) headers['X-CSRF-Token'] = csrf

  const response = await fetch(`/api/v1/agent/${options.sessionId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers,
    signal: options.signal,
    body: JSON.stringify({
      message: options.message,
      room_id: options.roomId ?? null,
      mask_id: options.maskId ?? null,
    }),
  })

  if (!response.ok || !response.body) {
    options.onError?.('The assistant is not answering right now.')
    options.onDone([])
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // SSE frames are separated by a blank line and each `data:` line is one JSON event. Parsed
  // by hand because the payload is a POST response, which no SSE client will read.
  //
  // `\r` is normalised away first. sse-starlette encodes frames with CRLF — `event: x\r\n
  // data: {...}\r\n\r\n` — and a parser that splits on `\n\n` finds nothing, forever: the
  // request stays open, the reply never appears, and the UI sits on "Thinking…" while the
  // server has already finished. It looked exactly like a buffering problem for an afternoon.
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')

    let split = buffer.indexOf('\n\n')
    while (split !== -1) {
      const frame = buffer.slice(0, split)
      buffer = buffer.slice(split + 2)
      split = buffer.indexOf('\n\n')

      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue
        let payload: AgentEvent
        try {
          payload = JSON.parse(line.slice(5).trim()) as AgentEvent
        } catch {
          continue
        }
        if (payload.event === 'token' && payload.text) options.onToken(payload.text)
        else if (payload.event === 'tool' && payload.tool) options.onTool?.(payload.tool)
        else if (payload.event === 'error') options.onError?.(payload.message ?? 'error')
        else if (payload.event === 'done') options.onDone(payload.ui_actions ?? [])
      }
    }
  }
}

export function useAgent(sessionId: string) {
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [busy, setBusy] = useState(false)
  const [tool, setTool] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const send = useCallback(
    async (
      text: string,
      context: { roomId?: string | null; maskId?: string | null },
      onActions?: (actions: UIAction[]) => void,
    ) => {
      if (!text.trim() || busy) return
      const id = crypto.randomUUID()
      setMessages((current) => [
        ...current,
        { id, role: 'user', content: text },
        { id: `${id}-reply`, role: 'assistant', content: '', pending: true },
      ])
      setBusy(true)
      abortRef.current = new AbortController()

      await sendMessage({
        sessionId,
        message: text,
        roomId: context.roomId,
        maskId: context.maskId,
        signal: abortRef.current.signal,
        onTool: setTool,
        onToken: (chunk) =>
          setMessages((current) =>
            current.map((message) =>
              message.id === `${id}-reply`
                ? { ...message, content: message.content + chunk }
                : message,
            ),
          ),
        onError: (problem) =>
          setMessages((current) =>
            current.map((message) =>
              message.id === `${id}-reply` && !message.content
                ? { ...message, content: problem }
                : message,
            ),
          ),
        onDone: (actions) => {
          setMessages((current) =>
            current.map((message) =>
              message.id === `${id}-reply` ? { ...message, pending: false, actions } : message,
            ),
          )
          setBusy(false)
          setTool(null)
          onActions?.(actions)
        },
      })
    },
    [sessionId, busy],
  )

  const load = useCallback(async () => {
    const response = await fetch(`/api/v1/agent/${sessionId}/transcript`, {
      credentials: 'include',
    })
    if (!response.ok) return
    const rows = (await response.json()) as {
      id: string
      role: 'user' | 'assistant'
      content: string
      ui_actions: UIAction[]
    }[]
    setMessages(
      rows.map((row) => ({
        id: row.id,
        role: row.role,
        content: row.content,
        actions: row.ui_actions,
      })),
    )
  }, [sessionId])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    setBusy(false)
  }, [])

  return { messages, busy, tool, send, load, stop }
}
